import { useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useSipPeers } from '../../../../hooks/useSipPeers';
import { usePeerGroups } from '../../../../hooks/usePeerGroups';
import {
  FormField,
  FormInput,
  FormSelect,
  SectionCard,
  StepHeader,
  InlineLoading,
} from '../../../common';
import type { RoutingRuleFormData } from '../schema';

/** Source tab: resolved source fields first, legacy fields collapsed below. */
export function SourceTab({ form }: { form: UseFormReturn<RoutingRuleFormData> }) {
  const sourceScope = form.watch('sourceScope') ?? 'any';

  const { data: peersResponse, isLoading: peersLoading } = useSipPeers();
  const { data: groupsResponse, isLoading: groupsLoading } = usePeerGroups();

  const peers = peersResponse?.data || [];
  const peerGroups = groupsResponse?.data || [];

  return (
    <div className="space-y-6">
      <StepHeader
        title="Source Matching"
        description="Resolved source identity is the primary classifier input."
      />

      <SectionCard
        title="Resolved source"
        variant="blue"
        bordered
        description="Choose the resolved identity the classifier should use first."
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField label="Source scope" required>
            <FormSelect
              value={sourceScope}
              onChange={(event) => {
                const next = event.target.value as RoutingRuleFormData['sourceScope'];
                form.setValue('sourceScope', next);
                if (next === 'sip_peer') {
                  form.setValue('sourcePeerGroupId', null);
                }
                if (next === 'peer_group') {
                  form.setValue('sourcePeerId', null);
                }
                if (next === 'any') {
                  form.setValue('sourcePeerId', null);
                  form.setValue('sourcePeerGroupId', null);
                }
              }}
            >
              <option value="any">Any source</option>
              <option value="sip_peer">Specific SIP peer</option>
              <option value="peer_group">Peer group</option>
            </FormSelect>
          </FormField>

          {sourceScope === 'sip_peer' && (
            <FormField
              label="Source SIP peer"
              required
              error={form.formState.errors.sourcePeerId?.message}
            >
              {peersLoading ? (
                <InlineLoading message="Loading peers..." />
              ) : (
                <FormSelect
                  value={form.watch('sourcePeerId') || ''}
                  onChange={(event) => {
                    form.setValue('sourcePeerId', event.target.value || null);
                    form.clearErrors('sourcePeerId');
                  }}
                  hasError={!!form.formState.errors.sourcePeerId}
                >
                  <option value="">Select a SIP peer...</option>
                  {peers.map((peer) => (
                    <option key={peer.id} value={peer.id}>
                      {peer.name} ({peer.type})
                    </option>
                  ))}
                </FormSelect>
              )}
            </FormField>
          )}

          {sourceScope === 'peer_group' && (
            <FormField
              label="Source peer group"
              required
              error={form.formState.errors.sourcePeerGroupId?.message}
            >
              {groupsLoading ? (
                <InlineLoading message="Loading groups..." />
              ) : (
                <FormSelect
                  value={form.watch('sourcePeerGroupId') || ''}
                  onChange={(event) => {
                    form.setValue('sourcePeerGroupId', event.target.value || null);
                    form.clearErrors('sourcePeerGroupId');
                  }}
                  hasError={!!form.formState.errors.sourcePeerGroupId}
                >
                  <option value="">Select a peer group...</option>
                  {peerGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name} ({group.peerCount} peers)
                    </option>
                  ))}
                </FormSelect>
              )}
            </FormField>
          )}
        </div>
          </SectionCard>

      <TimeBasedConditions form={form} />
    </div>
  );
}

/** Time-based conditions sub-component with enable/disable toggle. */
function TimeBasedConditions({ form }: { form: UseFormReturn<RoutingRuleFormData> }) {
  const timeWindowStart = form.watch('conditions.timeWindow.start');
  const timeWindowEnd = form.watch('conditions.timeWindow.end');
  const hasTimeWindow = !!timeWindowStart;

  const [enableTimeRestriction, setEnableTimeRestriction] = useState(hasTimeWindow);

  const startAfterEnd =
    !!timeWindowStart &&
    !!timeWindowEnd &&
    String(timeWindowStart) >= String(timeWindowEnd);

  const handleToggle = (enabled: boolean) => {
    setEnableTimeRestriction(enabled);
    if (!enabled) {
      form.setValue('conditions.timeWindow', undefined);
    }
  };

  return (
    <SectionCard
      title="Time Restrictions"
      description={enableTimeRestriction
        ? 'This rule only applies during the specified hours.'
        : 'This rule applies 24/7, all day every day.'}
      headerAction={
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={enableTimeRestriction}
            onChange={(event) => handleToggle(event.target.checked)}
          />
          Restrict to specific hours
        </label>
      }
    >
      {enableTimeRestriction && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField label="Start Time">
              <FormInput
                {...form.register('conditions.timeWindow.start')}
                type="time"
              />
            </FormField>

            <FormField label="End Time">
              <FormInput
                {...form.register('conditions.timeWindow.end')}
                type="time"
              />
            </FormField>

            <FormField label="Timezone">
              <FormInput
                {...form.register('conditions.timeWindow.timezone')}
                placeholder="America/New_York"
              />
            </FormField>
          </div>

          {startAfterEnd && (
            <div className="mt-3 text-xs text-amber-700">
              The selected window is empty or inverted; double-check the start and end times.
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}
